/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom types.
 */
import {ToggleSwitchProps} from './TogglerTypes';

/**
 * Import custom styles.
 */
import {ToggleSwitchContainer, ToggleSwitch as Switch, StyledSwitch} from './TogglerStyles';
import {COLORS, Size, SystemIcons} from '..';
import {useFocusVisibleRef} from '../common';
import styled from "styled-components";

const CheckmarkContainer =  styled.div`
  position: absolute;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 2px;
`;

const ToggleSwitch = React.forwardRef(({
                                         id,
                                         disabled,
                                         selected,
                                         label,
                                         isSemantic,
                                         size,
                                         onToggle,
                                         className,
                                         ...rest
                                       }: ToggleSwitchProps, ref) => {
  /**
   * Does all required pre-requisites and toggles the switcher state.
   */
  const toggle = () => {
    // If disabled, don't do anything
    if (disabled) return;

    // Toggle state change
    onToggle(!selected);
  };

  const focusVisibleRef = useFocusVisibleRef([]);

  React.useImperativeHandle(ref, () => focusVisibleRef.current, [focusVisibleRef]);

  size = size ?? Size.Medium;

  return (
    <StyledSwitch
      ref={focusVisibleRef}
      onClick={(e) => toggle()}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle()}
      className={size
        .toString()
        .toLowerCase()
        .concat(disabled ? ' disabled' : '')
        .concat(className ? ` ${className}` : '')}
      selected={selected}
      id={id}
      role="switch"
      aria-pressed={selected}
      tabIndex={disabled ? -1 : 0}
      {...rest}>
      <ToggleSwitchContainer id="switchContainer">
        <Switch className={isSemantic ? 'semantic' : ''} >
          {selected && <CheckmarkContainer><SystemIcons.CheckMark size={'14px'} color={COLORS.white}/></CheckmarkContainer>}
        </Switch>
      </ToggleSwitchContainer>
      {label && (
        <label className={'label'} htmlFor={id}>
          {label}
        </label>
      )}
    </StyledSwitch>
  );
});

export default ToggleSwitch;
